/**
 * DataQualityWidget - Phase 3.1 Dashboard Enhancement
 * 
 * Shows data completeness score with a visual progress ring.
 * Lists missing document types and their impact on the claim.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  CheckCircle,
  AlertCircle,
  FileText,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';

interface MissingDocument {
  type: string;
  label: string;
  impact: 'critical' | 'high' | 'medium' | 'low';
  description: string;
}

interface DataQualityData {
  score: number; // 0-100
  documentsProvided: number;
  documentsRequired: number;
  missingDocuments: MissingDocument[];
  lastUpdated: string;
  scope?: 'single' | 'portfolio'; // 'portfolio' = aggregate across active cases
  caseCount?: number;             // portfolio scope: # of active cases assessed
}

interface DataQualityWidgetProps {
  draftId?: string;
  reportId?: string;
  className?: string;
  compact?: boolean;
}

const IMPACT_COLORS: Record<string, { bgStyle: React.CSSProperties; textStyle: React.CSSProperties; dotStyle: React.CSSProperties }> = {
  critical: {
    bgStyle: { backgroundColor: 'var(--color-needs-human-tint)' },
    textStyle: { color: 'var(--color-needs-human-text)' },
    dotStyle: { backgroundColor: 'var(--color-brand)' },
  },
  high: {
    bgStyle: { backgroundColor: 'var(--color-needs-human-tint)' },
    textStyle: { color: 'var(--color-brand)' },
    dotStyle: { backgroundColor: 'var(--color-brand)' },
  },
  medium: {
    bgStyle: { backgroundColor: 'var(--color-needs-human-tint)' },
    textStyle: { color: 'var(--color-brand)' },
    dotStyle: { backgroundColor: 'var(--color-brand)' },
  },
  low: {
    bgStyle: { backgroundColor: 'var(--color-bg-elevated)' },
    textStyle: { color: 'var(--color-text-secondary)' },
    dotStyle: { backgroundColor: 'var(--color-text-dim)' },
  },
};

function ProgressRing({ score: rawScore, size = 120 }: { score: number; size?: number }) {
  // A degraded payload can omit `score` (undefined) — score/100 is then NaN and
  // React warns "Received NaN for the strokeDashoffset attribute" (and renders "NaN%").
  // Clamp to a finite 0–100.
  const score = Number.isFinite(rawScore) ? Math.min(100, Math.max(0, rawScore)) : 0;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  
  // Color based on score
  const getColor = () => {
    if (score >= 80) return 'var(--color-verified)'; // done/success green
    if (score >= 60) return 'var(--color-brand)'; // orange
    if (score >= 40) return 'var(--color-brand)'; // orange
    return 'var(--color-needs-human-text)'; // orange brand
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{score}%</span>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Complete</span>
      </div>
    </div>
  );
}

export default function DataQualityWidget({ 
  draftId, 
  reportId, 
  className = '',
  compact = false 
}: DataQualityWidgetProps) {
  const { getToken } = useAuth();
  const [data, setData] = useState<DataQualityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchDataQuality();
  }, [draftId, reportId]);

  const fetchDataQuality = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (draftId) params.append('draft_id', draftId);
      if (reportId) params.append('report_id', reportId);
      
      const result = await api.get(`/api/v1/dashboard/data-quality?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setData(result);
    } catch (err) {
      // Fallback to mock data if API not available
      console.debug('Data quality API not available, using fallback');
      setData({
        score: 75,
        documentsProvided: 6,
        documentsRequired: 8,
        missingDocuments: [
          { type: 'tax_returns', label: 'Tax Returns', impact: 'high', description: 'Needed for revenue verification' },
          { type: 'lease_agreement', label: 'Lease Agreement', impact: 'medium', description: 'Helps establish fixed costs' }
        ],
        lastUpdated: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`rounded-xl shadow-sm border p-6 ${className}`}
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        </div>
      </div>
    );
  }

  // Absent OR degraded payload → explicit on-brand unavailable card (never a
  // silent null — that fake-empty is banned). The page-sweep (2026-07-28) found
  // this widget on /queue, /issues, /review-queue and the DS queue; when its
  // endpoint returns a partial object, missingDocuments is undefined and
  // the raw .filter blanked the WHOLE page to the global ErrorBoundary.
  if (!data) {
    return (
      <div
        className={`rounded-xl shadow-sm border p-6 ${className}`}
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Data Quality</h3>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Data quality unavailable</p>
      </div>
    );
  }

  // Sub-field guard: a partial payload (data present, missingDocuments absent)
  // is the real crash, not a null data. Default the array once and use it below.
  const missingDocuments = Array.isArray(data.missingDocuments) ? data.missingDocuments : [];
  const criticalMissing = missingDocuments.filter(d => d.impact === 'critical' || d.impact === 'high');

  return (
    <div
      className={`rounded-xl shadow-sm border p-6 ${className}`}
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Data Quality</h3>
        <div className="flex items-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
          <FileText className="w-4 h-4 mr-1" />
          {data.scope === 'portfolio'
            ? `${data.caseCount ?? 0} active case${(data.caseCount ?? 0) !== 1 ? 's' : ''}`
            : `${data.documentsProvided}/${data.documentsRequired} docs`}
        </div>
      </div>

      <div className="flex items-center justify-center mb-4">
        <ProgressRing score={data.score} size={compact ? 100 : 120} />
      </div>

      {/* Status message */}
      <div
        className="text-center mb-4 p-3 rounded-lg"
        style={
          data.score >= 80
            ? { backgroundColor: 'transparent', color: 'var(--color-verified)' }
            : data.score >= 60
            ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
            : { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
        }
      >
        {data.score >= 80 ? (
          <div className="flex items-center justify-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span className="font-medium">Data quality is excellent</span>
          </div>
        ) : data.score >= 60 ? (
          <div className="flex items-center justify-center">
            <Info className="w-5 h-5 mr-2" />
            <span className="font-medium">Good progress, some documents missing</span>
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span className="font-medium">Critical documents needed</span>
          </div>
        )}
      </div>

      {/* Missing documents */}
      {missingDocuments.length > 0 && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-sm font-medium py-2"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <span>
              {criticalMissing.length > 0 
                ? `${criticalMissing.length} critical document${criticalMissing.length !== 1 ? 's' : ''} missing`
                : `${missingDocuments.length} document${missingDocuments.length !== 1 ? 's' : ''} missing`
              }
            </span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {expanded && (
            <div className="mt-2 space-y-2">
              {missingDocuments.map((doc, index) => {
                const colors = IMPACT_COLORS[doc.impact];
                return (
                  <div
                    key={index}
                    className="p-3 rounded-lg border border-opacity-50"
                    style={colors.bgStyle}
                  >
                    <div className="flex items-start">
                      <div className="w-2 h-2 rounded-full mt-1.5 mr-2 flex-shrink-0" style={colors.dotStyle} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium" style={colors.textStyle}>{doc.label}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ ...colors.bgStyle, ...colors.textStyle }}>
                            {doc.impact}
                          </span>
                        </div>
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{doc.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
